import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { UpgradeModal } from "@/components/UpgradeModal";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import GeneratePage from "@/pages/GeneratePage";
import CreateHubPage from "@/pages/CreateHubPage";
import ChatPage from "@/pages/ChatPage";
import DashboardPage from "@/pages/DashboardPage";
import HistoryPage from "@/pages/HistoryPage";
import SettingsPage from "@/pages/SettingsPage";
import NewsletterBuilderPage from "@/pages/NewsletterBuilderPage";
import LeadMagnetBuilderPage from "@/pages/LeadMagnetBuilderPage";
import SequencePlannerPage from "@/pages/SequencePlannerPage";
import BulkGeneratePage from "@/pages/BulkGeneratePage";
import LegalPage from "@/pages/LegalPage";
import PricingPage from "@/pages/PricingPage";
import CalendarPage from "@/pages/CalendarPage";
import InsightsPage from "@/pages/InsightsPage";
import ReportsPage from "@/pages/ReportsPage";
import ReportBuilderPage from "@/pages/ReportBuilderPage";
import VoiceWizardPage from "@/pages/VoiceWizardPage";
import VoiceHistoryPage from "@/pages/VoiceHistoryPage";
import BriefBuilderPage from "@/pages/BriefBuilderPage";
import BriefReviewPage from "@/pages/BriefReviewPage";
import BriefsListPage from "@/pages/BriefsListPage";
import ReviewPortalPage from "@/pages/ReviewPortalPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import PlansListPage from "@/pages/PlansListPage";
import PlanWizardPage from "@/pages/PlanWizardPage";
import PlanBuilderPage from "@/pages/PlanBuilderPage";
import FairUsePolicyPage from "@/pages/FairUsePolicyPage";
import BillingSuccessPage from "@/pages/BillingSuccessPage";
import ClientDashboard from "@/pages/ClientDashboard";
import StrategyTemplatesPage from "@/pages/StrategyTemplatesPage";
import ABTestPage from "@/pages/ABTestPage";
import CommunityComposerPage from "@/pages/CommunityComposerPage";
import BlogStudioPage from "@/pages/BlogStudioPage";
import AdsStudioPage from "@/pages/AdsStudioPage";
import PressReleaseBuilderPage from "@/pages/PressReleaseBuilderPage";
import GbpPostBuilderPage from "@/pages/GbpPostBuilderPage";
import TalkingDocPage from "@/pages/TalkingDocPage";
import GraphicGeneratorPage from "@/pages/GraphicGeneratorPage";
import ImageLibraryPage from "@/pages/ImageLibraryPage";
import ContentStudioPage from "@/pages/ContentStudioPage";
import SharedTalkingDocPage from "@/pages/SharedTalkingDocPage";
import CookieBanner from "@/components/CookieBanner";
import { OnboardingWizard } from "@/components/OnboardingWizard";

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/billing/success" element={<ProtectedRoute><BillingSuccessPage /></ProtectedRoute>} />
      <Route path="/legal/:docType" element={<LegalPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/create" element={<ProtectedRoute><CreateHubPage /></ProtectedRoute>} />
      <Route path="/generate" element={<ProtectedRoute><GeneratePage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/newsletter" element={<ProtectedRoute><NewsletterBuilderPage /></ProtectedRoute>} />
      <Route path="/lead-magnet" element={<ProtectedRoute><LeadMagnetBuilderPage /></ProtectedRoute>} />
      <Route path="/sequence" element={<ProtectedRoute><SequencePlannerPage /></ProtectedRoute>} />
      <Route path="/bulk" element={<ProtectedRoute><BulkGeneratePage /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/insights" element={<ProtectedRoute><InsightsPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/reports/:id" element={<ProtectedRoute><ReportBuilderPage /></ProtectedRoute>} />
      <Route path="/brand/voice-wizard" element={<ProtectedRoute><VoiceWizardPage /></ProtectedRoute>} />
      <Route path="/brand/voice-history" element={<ProtectedRoute><VoiceHistoryPage /></ProtectedRoute>} />
      <Route path="/settings/personalization/voice-wizard" element={<ProtectedRoute><VoiceWizardPage /></ProtectedRoute>} />
      <Route path="/settings/personalization/voice-history" element={<ProtectedRoute><VoiceHistoryPage /></ProtectedRoute>} />
      <Route path="/briefs" element={<ProtectedRoute><BriefsListPage /></ProtectedRoute>} />
      <Route path="/briefs/new" element={<ProtectedRoute><BriefBuilderPage /></ProtectedRoute>} />
      <Route path="/briefs/:id" element={<ProtectedRoute><BriefReviewPage /></ProtectedRoute>} />
      <Route path="/approvals" element={<ProtectedRoute><ApprovalsPage /></ProtectedRoute>} />
      <Route path="/plans" element={<ProtectedRoute><PlansListPage /></ProtectedRoute>} />
      <Route path="/plans/new" element={<ProtectedRoute><PlanWizardPage /></ProtectedRoute>} />
      <Route path="/plans/:id" element={<ProtectedRoute><PlanBuilderPage /></ProtectedRoute>} />
      <Route path="/review/:token" element={<ReviewPortalPage />} />
      <Route path="/share/talking-doc/:shareId" element={<SharedTalkingDocPage />} />
      <Route path="/fair-use-policy" element={<FairUsePolicyPage />} />
      <Route path="/client" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
      <Route path="/strategy/templates" element={<ProtectedRoute><StrategyTemplatesPage /></ProtectedRoute>} />
      <Route path="/ab-test" element={<ProtectedRoute><ABTestPage /></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute><CommunityComposerPage /></ProtectedRoute>} />
      <Route path="/blog-studio" element={<ProtectedRoute><BlogStudioPage /></ProtectedRoute>} />
      <Route path="/ads-studio" element={<ProtectedRoute><AdsStudioPage /></ProtectedRoute>} />
      <Route path="/press-release" element={<ProtectedRoute><PressReleaseBuilderPage /></ProtectedRoute>} />
      <Route path="/gbp-posts" element={<ProtectedRoute><GbpPostBuilderPage /></ProtectedRoute>} />
      <Route path="/talking-doc" element={<ProtectedRoute><TalkingDocPage /></ProtectedRoute>} />
      <Route path="/graphics" element={<ProtectedRoute><GraphicGeneratorPage /></ProtectedRoute>} />
      <Route path="/image-library" element={<ProtectedRoute><ImageLibraryPage /></ProtectedRoute>} />
      <Route path="/content-studio" element={<ProtectedRoute><ContentStudioPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
    </Routes>
  );
}

function GlobalUpgradeModal() {
  const { upgradeModal, closeUpgradeModal } = useAuth();
  return (
    <UpgradeModal
      isOpen={upgradeModal.open}
      onClose={closeUpgradeModal}
      errorDetail={upgradeModal.errorDetail}
    />
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  return (
    <Toaster
      position="top-right"
      toastOptions={{
        style: {
          background: theme === "dark" ? "#1A1F2D" : "#FFFFFF",
          border: theme === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
          color: theme === "dark" ? "#FFFFFF" : "#111827",
          boxShadow: theme === "dark" ? "none" : "0 4px 12px rgba(0,0,0,0.08)",
        },
      }}
    />
  );
}

import FeedbackButton from "./components/FeedbackButton";

function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRouter />
          <GlobalUpgradeModal />
          <OnboardingWizard />
          <CookieBanner />
          <FeedbackButton />
          <ThemedToaster />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
